import styled from "styled-components";
import { colors } from "../app/styles";

interface QuantityStepperProps {
  value: number;
  onChange: (value: number) => void;
  min?: number;
  disabled?: boolean;
  label?: string;
  /** Shown under the control, e.g. the order quantity for reference. */
  hint?: string;
  /** Emphasise the control (primary border) as the current step to complete. */
  active?: boolean;
}

const Wrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const Label = styled.label`
  font-weight: 700;
  font-size: 14px;
  color: ${colors.primaryDark};
`;

const Row = styled.div`
  display: flex;
  align-items: stretch;
  gap: 12px;
`;

const StepButton = styled.button`
  flex: 0 0 auto;
  width: 64px;
  height: 64px;
  border-radius: 14px;
  border: 2px solid ${colors.primary};
  background: #e0fbee;
  color: ${colors.primaryDark};
  font-size: 30px;
  font-weight: 700;
  line-height: 1;
  cursor: pointer;

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
  &:active:not(:disabled) {
    transform: scale(0.96);
  }
`;

const ValueInput = styled.input<{ $active?: boolean }>`
  flex: 1 1 auto;
  width: 100%;
  min-width: 0;
  text-align: center;
  height: 64px;
  border-radius: 14px;
  border: 2px solid ${(p) => (p.$active ? colors.primary : "#cfd8d4")};
  box-shadow: ${(p) =>
    p.$active ? `0 0 0 3px rgba(7, 165, 104, 0.18)` : "none"};
  background: #ffffff;
  font-size: 30px;
  font-weight: 800;
  color: ${colors.primaryDark};

  &:focus {
    border-color: ${colors.primary};
    outline: none;
  }
  &:disabled {
    opacity: 0.5;
  }
`;

const Hint = styled.span`
  font-size: 13px;
  color: ${colors.textMuted};
`;

export default function QuantityStepper({
  value,
  onChange,
  min = 0,
  disabled,
  label,
  hint,
  active,
}: QuantityStepperProps) {
  const dec = () => onChange(Math.max(min, value - 1));
  const inc = () => onChange(value + 1);
  const handleInput = (raw: string) => {
    const digits = raw.replace(/\D/g, "");
    onChange(digits === "" ? min : Math.max(min, parseInt(digits, 10)));
  };
  return (
    <Wrap>
      {label && <Label>{label}</Label>}
      <Row>
        <StepButton
          type="button"
          aria-label="Decrease"
          onClick={dec}
          disabled={disabled || value <= min}
        >
          −
        </StepButton>
        <ValueInput
          type="text"
          inputMode="numeric"
          aria-label={label ?? "Quantity"}
          value={String(value)}
          disabled={disabled}
          $active={active}
          onFocus={(e) => e.target.select()}
          onChange={(e) => handleInput(e.target.value)}
        />
        <StepButton
          type="button"
          aria-label="Increase"
          onClick={inc}
          disabled={disabled}
        >
          +
        </StepButton>
      </Row>
      {hint && <Hint>{hint}</Hint>}
    </Wrap>
  );
}
